import * as Yup from "yup";

import { ENV } from "../../../../../utils/constants";

export function getTodayDate() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");

  return `${now.getFullYear()}-${month}-${day}`;
}

export function getInitialValues() {
  return {
    reference: "",
    notes: "",
    saleDate: getTodayDate(),
    items: [],
  };
}

export function createLocalId() {
  if (
    typeof crypto !== "undefined" &&
    typeof crypto.randomUUID === "function"
  ) {
    return crypto.randomUUID();
  }

  return `inventory-exit-item-${Date.now()}-${Math.random()
    .toString(16)
    .slice(2)}`;
}

export function getImageUrl(imagePath) {
  if (!imagePath) {
    return "";
  }

  const value = String(imagePath);

  if (
    value.startsWith("http://") ||
    value.startsWith("https://") ||
    value.startsWith("blob:")
  ) {
    return value;
  }

  return `${ENV.SERVER_HOST}/${value
    .replace(/\\/g, "/")
    .replace(/^\/+/, "")}`;
}

export function getProductImage(product) {
  const images = Array.isArray(product?.images)
    ? product.images
    : [];

  const coverImage =
    images.find((image) => image?.isCover) || images[0];

  return getImageUrl(coverImage?.path);
}

export function getAttributeName(attribute) {
  if (attribute?.schema && typeof attribute.schema === "object") {
    return attribute.schema.name || "";
  }

  return attribute?.name || "";
}

export function getAttributeValue(attribute) {
  if (
    attribute?.classification &&
    typeof attribute.classification === "object"
  ) {
    return (
      attribute.classification.name ||
      attribute.classification.value ||
      attribute.value ||
      ""
    );
  }

  return attribute?.label || attribute?.value || "";
}

export function getVariantLabel(variant) {
  const attributes = Array.isArray(variant?.attributes)
    ? variant.attributes
    : [];

  if (attributes.length === 0) {
    return "Variante única";
  }

  return attributes
    .map((attribute) => {
      const name = getAttributeName(attribute);
      const value = getAttributeValue(attribute);

      if (name && value) {
        return `${name}: ${value}`;
      }

      return value || name;
    })
    .filter(Boolean)
    .join(" · ");
}

export function getAvailableStock(variant) {
  return Math.max(Number(variant?.stock) || 0, 0);
}

export function flattenProducts(products = []) {
  if (!Array.isArray(products)) {
    return [];
  }

  return products.flatMap((product) => {
    const productId = String(product?._id || "").trim();

    if (!productId) {
      return [];
    }

    const variants = Array.isArray(product?.variants)
      ? product.variants
      : [];

    return variants
      .filter(
        (variant) =>
          variant?.enabled !== false &&
          Boolean(variant?._id)
      )
      .map((variant) => ({
        productId,
        productCode: String(product?.id || ""),
        productName: String(product?.name || ""),
        productImage: getProductImage(product),
        variantId: String(variant._id),
        sku: String(variant?.sku || ""),
        variantLabel: getVariantLabel(variant),
        stock: Number(variant?.stock) || 0,
        availableStock: getAvailableStock(variant),
        price: Number(variant?.price) || 0,
        offerPrice:
          variant?.offerPrice === null || variant?.offerPrice === undefined
            ? null
            : Number(variant.offerPrice) || 0,
        cost: Number(variant?.cost) || 0,
      }));
  });
}

export function createExitItem(existence = {}) {
  const availableStock = Math.max(
    Number(existence.availableStock) || 0,
    0
  );

  const suggestedPrice =
    existence.offerPrice !== null && existence.offerPrice !== undefined
      ? existence.offerPrice
      : existence.price;

  return {
    localId: createLocalId(),

    product: String(existence.productId || "").trim(),
    variantId: String(existence.variantId || "").trim(),

    productName: existence.productName || "",
    productCode: existence.productCode || "",
    productImage: existence.productImage || "",
    sku: existence.sku || "",
    variantLabel: existence.variantLabel || "Variante única",

    stock: Number(existence.stock) || 0,
    availableStock,
    cost: Number(existence.cost) || 0,

    quantity: availableStock > 0 ? 1 : "",
    unitPrice: Number(suggestedPrice) || 0,
  };
}

export function formatNumber(value) {
  return (Number(value) || 0).toLocaleString("es-GT");
}

export function formatCurrency(value) {
  return `Q ${(Number(value) || 0).toLocaleString("es-GT", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

const inventoryExitItemSchema = Yup.object({
  localId: Yup.string().required(),

  product: Yup.string()
    .trim()
    .required("El producto es obligatorio."),

  variantId: Yup.string()
    .trim()
    .required("La variante es obligatoria."),

  availableStock: Yup.number()
    .min(0)
    .required(),

  quantity: Yup.number()
    .typeError("La cantidad debe ser un número válido.")
    .integer("La cantidad debe ser un número entero.")
    .min(1, "La cantidad debe ser mayor a 0.")
    .required("La cantidad es obligatoria.")
    .test(
      "available-stock",
      "La cantidad no puede ser mayor al stock disponible.",
      function validateAvailableStock(value) {
        const availableStock = Number(
          this.parent.availableStock
        );

        if (
          value === undefined ||
          value === null ||
          value === ""
        ) {
          return true;
        }

        return Number(value) <= availableStock;
      }
    ),

  unitPrice: Yup.number()
    .typeError("El precio de venta debe ser un número válido.")
    .min(0, "El precio de venta no puede ser negativo.")
    .required("El precio de venta es obligatorio."),
});

export const validationSchema = Yup.object({
  saleDate: Yup.string()
    .required("La fecha de venta es obligatoria.")
    .matches(/^\d{4}-\d{2}-\d{2}$/, "La fecha de venta no es válida.")
    .test(
      "not-future",
      "La fecha de venta no puede ser futura.",
      (value) => !value || value <= getTodayDate()
    ),

  reference: Yup.string()
    .trim()
    .max(100, "La referencia no puede superar 100 caracteres."),

  notes: Yup.string()
    .trim()
    .max(1000, "Las notas no pueden superar 1,000 caracteres."),

  items: Yup.array()
    .of(inventoryExitItemSchema)
    .min(1, "Agrega al menos un producto o variante.")
    .required("Agrega al menos un producto o variante.")
    .test(
      "unique-variants",
      "No puedes agregar la misma variante más de una vez.",
      (items) => {
        if (!Array.isArray(items)) {
          return true;
        }

        const keys = items.map(
          (item) => `${item.product}-${item.variantId}`
        );

        return new Set(keys).size === keys.length;
      }
    ),
});